import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { range } from 'lodash-es';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { ProductContextFacade } from 'ish-core/facades/product-context.facade';
import { ProductView } from 'ish-core/models/product-view/product-view.model';
import { whenTruthy } from 'ish-core/utils/operators';

import { CustomQuickorderFacade } from '../../facades/custom-quickorder.facade';

@Component({
  selector: 'custom-quick-order-quantity',
  templateUrl: './quick-order-quantity.component.html',
  styleUrls: ['./quick-order-quantity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickOrderQuantityComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('quantityInput') quantityInput: ElementRef<HTMLInputElement>;
  @Input() type: 'input' | 'select' | 'counter' = 'counter';
  @Input() id: string = uuid();
  @Input() lineItemID: string;
  @Input() focus: boolean;
  @Output() quantityUpdatedLineItemID: EventEmitter<string> = new EventEmitter<string>();

  private destroy$ = new Subject<void>();
  visible$: Observable<boolean>;
  quantity$: Observable<number>;
  min$: Observable<number>;
  max$: Observable<number>;
  step$: Observable<number>;
  hasQuantityError$: Observable<boolean>;
  quantityError$: Observable<string>;
  product$: Observable<ProductView>;
  selectValues$: Observable<number[]>;
  productIndex$: Observable<number>;

  cannotIncrease$: Observable<boolean>;
  cannotDecrease$: Observable<boolean>;
  quantityChange = new Subject<EventTarget>();

  constructor(
    private context: ProductContextFacade,
    private quickOrderFacade: CustomQuickorderFacade,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.visible$ = this.context.select('displayProperties', 'quantity');
    this.quantity$ = this.context.select('quantity').pipe(filter(n => typeof n === 'number' && !isNaN(n)));
    this.min$ = this.context.select('minQuantity');
    this.max$ = this.context.select('maxQuantity');
    this.step$ = this.context.select('stepQuantity');
    this.product$ = this.context.select('product');
    this.hasQuantityError$ = this.context.select('hasQuantityError');
    this.quantityError$ = this.context.select('quantityError');

    this.productIndex$ = this.context.select('productIndex');
    this.selectValues$ = combineLatest([this.min$, this.max$, this.step$]).pipe(
      map(([min, max, step]) => range(min, max + 1, step))
    );

    this.cannotIncrease$ = combineLatest([
      this.max$,
      this.quantity$,
      this.product$,
      this.context.select('loading'),
    ]).pipe(map(([max, quantity, product, loading]) => quantity >= max || product.failed || loading));
    this.cannotDecrease$ = combineLatest([
      this.min$,
      this.quantity$,
      this.product$,
      this.context.select('loading'),
    ]).pipe(map(([min, quantity, product, loading]) => quantity <= min || product.failed || loading));

    this.quantityChange.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(() => {
      this.changeOnEnter();
    });
  }

  ngAfterViewInit() {
    this.visible$.pipe(whenTruthy(), take(1), takeUntil(this.destroy$)).subscribe(visible => {
      if (this.focus && visible) {
        this.cdRef.detectChanges();
        const input = this.quantityInput;
        if (input) {
          const quantity = input.nativeElement;
          quantity.focus();
          this.cdRef.markForCheck();
        }
      }
    });
  }

  private setValue(value: number) {
    this.context.set('quantity', () => value);
  }

  productLoading() {
    return this.context.get('loading');
  }
  private setNextValue(value: number) {
    const max = this.context.get('maxQuantity');
    const min = this.context.get('minQuantity');
    const step = this.context.get('stepQuantity');
    this.setValue(value > max ? max : value < min ? min : value - (value % step));
  }

  increase() {
    if (!isNaN(this.context.get('quantity'))) {
      this.setNextValue(this.context.get('quantity') + this.context.get('stepQuantity'));
      if (this.lineItemID) {
        this.quickOrderFacade.updateQuickOrderItem(this.lineItemID, {
          productSKU: this.context.get('sku'),
          quantity: this.context.get('quantity'),
        });
        this.emitQuantityUpdatedEvent('');
      }
    }
  }

  decrease() {
    if (!isNaN(this.context.get('quantity'))) {
      this.setNextValue(this.context.get('quantity') - this.context.get('stepQuantity'));
      if (this.lineItemID) {
        this.quickOrderFacade.updateQuickOrderItem(this.lineItemID, {
          productSKU: this.context.get('sku'),
          quantity: this.context.get('quantity'),
        });
        this.emitQuantityUpdatedEvent('');
      }
    }
  }

  changeOnEnter() {
    if (this.lineItemID && this.context.get('quantity') > 0 && !isNaN(this.context.get('quantity'))) {
      this.quickOrderFacade.updateQuickOrderItem(this.lineItemID, {
        productSKU: this.context.get('sku'),
        quantity: this.context.get('quantity'),
      });
      this.emitQuantityUpdatedEvent('enter');
    }
  }

  change(target: EventTarget) {
    this.setValue(Number.parseInt((target as HTMLDataElement).value, 10));
  }

  emitQuantityUpdatedEvent(type: string) {
    if (type === 'enter') this.quantityUpdatedLineItemID.emit(this.lineItemID);
    else this.quantityUpdatedLineItemID.emit('changeThroughIncrementOrDecrement');
  }

  isButtonDisable$() {
    return combineLatest([this.product$, this.productIndex$]).pipe(
      map(([product, index]) => (product.sku && index > -1 ? true : false)),
      takeUntil(this.destroy$)
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
